import type { NavigateFunction } from "react-router-dom";
import patientRequest from "@/api/patient-request";
import taskRequest from "@/api/task-request";
import { getFacilityKey, getFacilityKeyForPortal, getPatientKey } from "@/store/profile";
import { useTaskStore } from "@/store/task";
import { buildFormHandlerLaunchPayload } from "@/types/portal-forms";
import { hasLaterHistoryStep, wizardNextPath, type WizardStep } from "@/hooks/useWizardFlow";
import {
  navigateToFormHandler,
  type FormHandlerLaunchState,
} from "@/utils/formHandlerLaunch";
import { isFormTask, type TaskItem } from "@/types/tasks";

/** FORM tasks still pending according to GetTasks (after prior tasks are marked complete). */
export async function loadPendingFormTasks(
  facilityKey: string,
  patientKey: string
): Promise<TaskItem[]> {
  if (!facilityKey || !patientKey) return [];
  try {
    const res = await patientRequest.request.gettasks(facilityKey, patientKey);
    const tasks = (res.data as TaskItem[]) ?? [];
    return tasks.filter((t) => isFormTask(t.taskType));
  } catch {
    return [];
  }
}

export function buildFirstFormHandlerLaunchPayload(
  formTasks: TaskItem[]
): FormHandlerLaunchState | null {
  if (formTasks.length === 0) return null;
  return buildFormHandlerLaunchPayload(formTasks[0], formTasks.length);
}

/**
 * Loads tasks and navigates to the first FORM task (same contract as dashboard DashTask).
 * Returns true if navigation occurred, false if redirected to home as fallback.
 */
export async function navigateToFirstFormTask(
  navigate: NavigateFunction
): Promise<boolean> {
  const facilityKey = getFacilityKey();
  const patientKey = getPatientKey();
  const formTasks = await loadPendingFormTasks(facilityKey, patientKey);
  const payload = buildFirstFormHandlerLaunchPayload(formTasks);
  if (!payload) {
    navigate("/");
    return false;
  }
  navigateToFormHandler(navigate, payload);
  return true;
}

/**
 * Leave a History-group step (custom config, complaints, General, Family).
 *
 * If another History step follows, go to it. Otherwise this was the last History step: for a
 * portal history task, mark the case history and the task complete here — and only here, so
 * earlier steps never close the task while later ones still have answers to save — then open
 * the first pending form, or the task-complete page. Throws if completion fails so the caller
 * can show the error and let the patient retry.
 */
export async function finishHistoryStep(
  navigate: NavigateFunction,
  wizardSteps: WizardStep[],
  currentPath: string,
  fallbackWhenNoNext: string,
  opts?: { replace?: boolean }
): Promise<void> {
  const next = wizardNextPath(wizardSteps, currentPath);
  if (next && hasLaterHistoryStep(wizardSteps, currentPath)) {
    navigate(next, { replace: opts?.replace });
    return;
  }

  const { portalCaseHistoryFlow, activePortalTaskId, caseHistory } = useTaskStore.getState();
  const facilityKey = getFacilityKeyForPortal();
  const patientKey = getPatientKey();
  const caseHistoryId = caseHistory?.id;
  if (portalCaseHistoryFlow && activePortalTaskId && caseHistoryId && facilityKey && patientKey) {
    await taskRequest.request.updateCaseHistoryComplete(facilityKey, patientKey, caseHistoryId);
    await taskRequest.request.updateTaskComplete(facilityKey, patientKey, activePortalTaskId);
    const payload = buildFirstFormHandlerLaunchPayload(
      await loadPendingFormTasks(facilityKey, patientKey)
    );
    if (payload) navigateToFormHandler(navigate, payload, { replace: opts?.replace });
    else navigate("/complaint/task-complete", { replace: opts?.replace });
    return;
  }

  // Not a portal history task: plain wizard order.
  await navigateComplaintWizardNext(navigate, wizardSteps, currentPath, fallbackWhenNoNext);
}

/**
 * After a complaint wizard step, go to the next path; if the next step is Forms,
 * resolve the first pending form launch payload instead of bare `/form-handler`.
 */
export async function navigateComplaintWizardNext(
  navigate: NavigateFunction,
  wizardSteps: WizardStep[],
  currentPath: string,
  fallbackWhenNoNext: string
): Promise<void> {
  const next = wizardNextPath(wizardSteps, currentPath);
  if (next === "/form-handler") {
    await navigateToFirstFormTask(navigate);
    return;
  }
  if (next) {
    navigate(next);
    return;
  }
  navigate(fallbackWhenNoNext);
}
